import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight newsletter prompt variants.
 * Each variant is a small inline component — no external deps, no heavy scripts.
 * Subscribes via /api/kit/subscribe endpoint.
 */

// Inline newsletter bar (for bottom of sections)
export const NewsletterInline = ({ hook, placeholder = 'your@email.com', buttonText = 'Join', className = '', testId = 'newsletter-inline', source = 'Site Fan' }) => {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);

  const subscribe = async () => {
    if (!email) return;
    try {
      await axios.post(`${API}/kit/subscribe`, { email, source });
      setDone(true);
    } catch {}
  };

  if (done) return (
    <div className={`text-center py-3 ${className}`} data-testid={`${testId}-done`}>
      <p className="text-sm font-bold text-green-600">You're in! Check your inbox.</p>
    </div>
  );

  return (
    <div className={`${className}`} data-testid={testId}>
      <p className="text-sm font-bold text-gray-800 mb-2">{hook}</p>
      <div className="flex gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && subscribe()}
          placeholder={placeholder} className="flex-1 border-2 border-gray-300 px-3 py-2.5 rounded-lg text-sm focus:border-[#FF6B35] focus:outline-none" />
        <button onClick={subscribe} className="bg-[#FF6B35] text-white px-4 py-2.5 rounded-lg font-bold text-xs uppercase hover:bg-[#FF8C42] flex-shrink-0 transition-colors">
          {buttonText}
        </button>
      </div>
    </div>
  );
};

// Exit intent overlay (shows once per session, lazy-loaded)
export const ExitIntentNewsletter = ({ subscriberCount = 3200 }) => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Only trigger once per session
    if (sessionStorage.getItem('wsp_exit_shown')) return;
    
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        setShow(true);
        sessionStorage.setItem('wsp_exit_shown', 'true');
        document.removeEventListener('mouseout', handleMouseLeave);
      }
    };

    // Delay attaching — don't trigger on first few seconds
    const timer = setTimeout(() => {
      document.addEventListener('mouseout', handleMouseLeave);
    }, 8000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, []);

  const subscribe = async () => {
    if (!email) return;
    try {
      await axios.post(`${API}/kit/subscribe`, { email, source: 'Exit Intent Fan' });
      setDone(true);
      setTimeout(() => setShow(false), 2000);
    } catch {}
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="exit-intent-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-[scaleIn_0.3s_ease-out]">
        <button onClick={() => setShow(false)} className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="exit-intent-close">
          <X size={20} weight="bold" className="text-gray-400" />
        </button>
        {!done ? (
          <>
            <h3 className="font-['Bebas_Neue'] text-3xl uppercase text-gray-900 mb-2">Wait! Don't Get Locked Out.</h3>
            <p className="text-sm text-gray-600 mb-4">
              Official zones will be gridlocked. Join <strong className="text-[#FF6B35]">{subscriberCount.toLocaleString()}+</strong> fans getting the insider updates for the Toronto core.
            </p>
            <div className="flex gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && subscribe()}
                placeholder="your@email.com" className="flex-1 border-2 border-gray-300 px-4 py-3 rounded-lg text-sm focus:border-[#FF6B35] focus:outline-none" data-testid="exit-intent-email" />
              <button onClick={subscribe} className="bg-[#FF6B35] text-white px-5 py-3 rounded-lg font-bold text-sm uppercase hover:bg-[#FF8C42] flex-shrink-0" data-testid="exit-intent-submit">
                Join the Party
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-2xl mb-2">You're in!</p>
            <p className="text-sm text-gray-600">Check your inbox for confirmation.</p>
          </div>
        )}
      </div>
    </div>
  );
};
